import {
  DXF_BENDING_ENTITY_TYPE,
  DXF_CO_APPLICABLE_ROLES,
  DXF_ENTITY_KEY_PATTERN,
  DXF_MAPPING_ROLE,
  type DxfEntity,
  type DxfEntityKey,
  type DxfEntityRoles,
  type DxfMappingRole,
} from './types';

/**
 * Stable mapping key for a scene entity, matching the backend `entity_key_of`.
 */
export const entityKeyOf = (
  entity: Pick<DxfEntity, 'h' | 'src'>,
): DxfEntityKey =>
  entity.src?.length ? `${entity.h}/${entity.src.join('/')}` : entity.h;

export const isValidEntityKey = (key: string): key is DxfEntityKey =>
  DXF_ENTITY_KEY_PATTERN.test(key);

export const isDxfMappingRole = (value: unknown): value is DxfMappingRole =>
  typeof value === 'string' &&
  (Object.values(DXF_MAPPING_ROLE) as string[]).includes(value);

/**
 * True when `roles` may occupy a single entity: empty or one role, or every
 * role is etching/bending with no duplicates.
 */
export const areRolesCoApplicable = (roles: readonly string[]): boolean => {
  if (roles.length === 0) {
    return true;
  }
  if (!roles.every(isDxfMappingRole)) {
    return false;
  }
  if (new Set(roles).size !== roles.length) {
    return false;
  }
  if (roles.length === 1) {
    return true;
  }
  return roles.every(
    (role) => isDxfMappingRole(role) && DXF_CO_APPLICABLE_ROLES.has(role),
  );
};

/**
 * True when `role` can be *added* to `current` without a round trip. False if
 * it is already present (duplicates are rejected) or the resulting set is not
 * co-applicable. Replacing an exclusive role is a UI decision — drop the old
 * role first, then call this.
 */
export const canAssignRole = (
  current: readonly DxfMappingRole[],
  role: DxfMappingRole,
): boolean => {
  if (current.includes(role)) {
    return false;
  }
  return areRolesCoApplicable([...current, role]);
};

export const isBendingAllowedForEntityType = (entityType: string): boolean =>
  entityType === DXF_BENDING_ENTITY_TYPE;

/**
 * Combine co-applicability with the bending-on-LINE rule when `entityType` is
 * known. Omit `entityType` to skip the geometric check.
 */
export const canAssignRoleToEntity = ({
  currentRoles,
  role,
  entityType,
}: {
  currentRoles: DxfEntityRoles;
  role: DxfMappingRole;
  entityType?: string;
}): boolean => {
  if (
    role === DXF_MAPPING_ROLE.BENDING &&
    entityType !== undefined &&
    !isBendingAllowedForEntityType(entityType)
  ) {
    return false;
  }
  return canAssignRole(currentRoles, role);
};

/** Pre-flight the write payload without hitting the API. */
export const isValidAssignments = (
  assignments: Readonly<Record<string, readonly string[]>>,
): boolean =>
  Object.entries(assignments).every(
    ([key, roles]) =>
      isValidEntityKey(key) && roles.length > 0 && areRolesCoApplicable(roles),
  );
